import { useAuthenticatedContextProvider } from '@contexts/AuthenticatedContextProvider';
import { TabPanel, TabPanels, Tabs, Tab, TabList, Flex, Heading, Text, Tag, HStack, Divider } from '@chakra-ui/react';
import Modal from '@components/crm/lead/shared/Modal';
import { sendFirstMeetingPortalInvite, sendMagicLink } from '@lib/requests';
import { AgencyName } from '@lib/types';
import { useMutation } from '@tanstack/react-query';
import { formatToUTCString } from '@utils/formatDate';
import { IANATimezones } from '@utils/timezones';
import { ErrorBoundary } from '@sentry/react';
import { formatInTimeZone } from 'date-fns-tz';
import { useEffect, useMemo, useState } from 'react';
import ErrorMessage from '@components/crm/lead/shared/ErrorMessage';
import 'react-quill/dist/quill.snow.css';
import useStandardToast from '../../lead/shared/useStandardToast';
import ScheduledEmails from './ScheduledEmails';
import { getTemplates } from './templates';
import { formatTemplate } from './utils';
import SendEmailForm from './SendEmailForm';

const SendEmailModal = ({
  isOpen,
  setIsOpen,
  userData,
  agent,
  sendAgentEmail,
  scheduleAgentEmail,
  scheduledEmails,
  deleteScheduledEmail,
}) => {
  // Resources
  const showToast = useStandardToast();
  const isFsm = agent.agency?.name === AgencyName.FAIR_SQUARE;
  const { authenticatedUser: user } = useAuthenticatedContextProvider();
  if (!userData || !user) return null;
  const templates = useMemo(
    () =>
      user ? getTemplates({ firstName: user.first_name || '', lastName: user.last_name || '', agent: user }) : null,
    [user]
  );

  // Non FSM clients can only send custom emails.
  const enabledTemplates = isFsm ? templates : { customEmail: templates.customEmail };
  const defaultTemplateKey = isFsm ? 'followUp' : 'customEmail';
  const defaultTemplate = enabledTemplates[defaultTemplateKey];
  const disableSend = user?.role !== 'AGENT';

  // State
  const [templateValue, setTemplateValue] = useState(
    formatTemplate(
      defaultTemplateKey,
      userData.firstName,
      userData.preferredName,
      agent.first_name,
      agent.last_name,
      user
    )
  );

  const [selectedTime, setSelectedTime] = useState({
    hour: '',
    minute: '',
    period: '',
  });
  const [templateSubject, setTemplateSubject] = useState(defaultTemplate.subject);
  const [htmlBody, setHtmlBody] = useState(templateValue);
  const [isLoading, setIsLoading] = useState(false);
  const [templateEnum, setTemplateEnum] = useState(defaultTemplate.enum);
  const [showScheduler, setShowScheduler] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [localDeliveryTime, setLocalDeliveryTime] = useState(null);
  const [refetchConversations, setRefetchConversations] = useState(true);

  // Queries
  const firstPortalInviteMutation = useMutation({
    mutationFn: sendFirstMeetingPortalInvite,
    onSuccess: () => {
      showToast('Portal Invite Sent!', 'success');
    },
    onError: (err) => {
      showToast('Error sending portal invite', 'error');
    },
  });

  const sendMagicLinkMutation = useMutation({
    mutationFn: sendMagicLink,
    onSuccess: () => {
      showToast('Magic Link Sent!', 'success');
    },
    onError: () => {
      showToast('Error sending Magic Link', 'error');
    },
  });

  // Side Effects
  useEffect(() => {
    const validDate = !!(selectedDate && selectedTime.hour && selectedTime.minute && selectedTime.period);
    if (showScheduler && validDate && userData.timezone) {
      const formattedTime = `${selectedTime.hour}:${selectedTime.minute} ${selectedTime.period}`;
      const dateTime = `${selectedDate} ${formattedTime}`;
      const timeZone = IANATimezones[userData.timezone];
      const converted = formatInTimeZone(new Date(dateTime), timeZone, 'Pp');
      setLocalDeliveryTime(converted);
    }
  }, [selectedDate, selectedTime, showScheduler, userData.timezone]);

  useEffect(() => {
    const newTemplateValue = formatTemplate(
      defaultTemplateKey,
      userData.firstName,
      userData.preferredName,
      agent.first_name,
      agent.last_name,
      user
    );
    setTemplateValue(newTemplateValue);
    setHtmlBody(newTemplateValue);
  }, [userData.firstName, userData.preferredName, agent.first_name, agent.last_name, defaultTemplateKey]);

  // Handlers
  const handleShowScheduler = () => setShowScheduler(!showScheduler);

  const handleClose = () => {
    resetFormState();
    setIsOpen(false);
  };

  const handleTemplateChange = (templateKey) => {
    setTemplateSubject(enabledTemplates[templateKey].subject);
    const formatted = formatTemplate(
      templateKey,
      userData.firstName,
      userData.preferredName,
      agent.first_name,
      agent.last_name,
      user
    );
    setTemplateValue(formatted);
    setHtmlBody(formatted);
    setTemplateEnum(enabledTemplates[templateKey].enum);
  };

  const resetFormState = () => {
    const newTemplateValue = formatTemplate(
      defaultTemplateKey,
      userData.firstName,
      userData.preferredName,
      agent.first_name,
      agent.last_name,
      user
    );
    setTemplateValue(newTemplateValue);
    setTemplateEnum(defaultTemplate.enum);
    setTemplateSubject(defaultTemplate.subject);
    setHtmlBody(newTemplateValue);
    setShowScheduler(false);
    setSelectedDate(null);
    setSelectedTime({
      hour: '',
      minute: '',
      period: '',
    });
    setLocalDeliveryTime(null);
    setRefetchConversations(true);
  };

  const handleSendEmail = async () => {
    setIsLoading(true);
    if (showScheduler) {
      const formattedTime = `${selectedTime.hour}:${selectedTime.minute} ${selectedTime.period}`;
      const dateTime = `${selectedDate} ${formattedTime}`;
      const utcTime = formatToUTCString(dateTime);
      await scheduleAgentEmail({
        variables: {
          isTest: false,
          notificationType: 'AGENT_GENERATED_EMAIL',
          templateType: templateEnum,
          overrideSubject: templateSubject,
          toEmail: userData.email,
          htmlBody,
          userId: userData.userId,
          scheduledDeliveryAt: new Date(utcTime),
        },
      });
    } else {
      await sendAgentEmail({
        variables: {
          userId: userData.userId,
          htmlBody,
          toEmail: userData.email,
          fromEmail: agent.email,
          replyToEmail: agent.email,
          overrideSubject: templateSubject,
          bcc: agent.email,
          isTest: false,
          notificationType: 'AGENT_GENERATED_EMAIL',
          templateType: templateEnum,
        },
      });
    }

    resetFormState();
    setIsLoading(false);
    setIsOpen(false);
  };

  const handleSendFirstPortalInvite = async () => {
    setIsLoading(true);
    await firstPortalInviteMutation.mutate({ userId: userData.userId, agentId: agent.id });
    resetFormState();
    setIsLoading(false);
    setIsOpen(false);
  };

  const handleSendMagicLink = async () => {
    setIsLoading(true);
    await sendMagicLinkMutation.mutate({ userId: userData.userId });
    resetFormState();
    setIsLoading(false);
    setIsOpen(false);
  };

  // Helpers
  const getUserLocalTime = () => {
    const timeZone = IANATimezones[userData.timezone];
    return formatInTimeZone(new Date(), timeZone, 'Pp');
  };

  const invalidScheduleArgs =
    showScheduler && (!selectedDate || !selectedTime.hour || !selectedTime.minute || !selectedTime.period);
  const invalidEmailArgs = !htmlBody || !templateSubject;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="5xl">
      <ErrorBoundary
        beforeCapture={(scope) => scope.setTag('Component', 'SendEmailModal')}
        fallback={<ErrorMessage label="Failed to load Send Email" />}
      >
        <Flex justifyContent="flex-start" mb={5}>
          <Heading size="md" mx={2}>
            {userData.userName}
          </Heading>
          <Divider orientation="vertical" />
          <Text fontSize="lg" mx={2}>
            {userData.email}
          </Text>
          {userData.timezone && (
            <HStack ml="auto">
              <Tag variant="outline" colorScheme="primary">
                {`Local Time: ${getUserLocalTime()}`}
              </Tag>
              <Tag size="md" variant="solid" colorScheme="primary">
                {userData.timezone}
              </Tag>
            </HStack>
          )}
        </Flex>
        <Tabs variant="enclosed" colorScheme="primary">
          <TabList>
            <Tab>Send Email</Tab>
            <Tab isDisabled={!scheduledEmails?.length}>Scheduled Emails</Tab>
          </TabList>
          <TabPanels minH="500">
            <TabPanel>
              <SendEmailForm
                handleSendEmail={handleSendEmail}
                handleSendMagicLink={handleSendMagicLink}
                templateSubject={templateSubject}
                setHtmlBody={setHtmlBody}
                enabledTemplates={enabledTemplates}
                handleTemplateChange={handleTemplateChange}
                showScheduler={showScheduler}
                templateValue={templateValue}
                setSelectedDate={setSelectedDate}
                handleShowScheduler={handleShowScheduler}
                localDeliveryTime={localDeliveryTime}
                firstPortalInviteMutation={firstPortalInviteMutation}
                isLoading={isLoading}
                handleSendFirstPortalInvite={handleSendFirstPortalInvite}
                invalidScheduleArgs={invalidScheduleArgs}
                invalidEmailArgs={invalidEmailArgs}
                isFsm={isFsm}
                setTemplateSubject={setTemplateSubject}
                selectedTime={selectedTime}
                setSelectedTime={setSelectedTime}
                isDisabled={disableSend}
              />
            </TabPanel>
            <TabPanel>
              <ScheduledEmails
                scheduledEmails={scheduledEmails}
                deleteScheduledEmail={deleteScheduledEmail}
                handleClose={handleClose}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </ErrorBoundary>
    </Modal>
  );
};
export default SendEmailModal;
